/**
 * Helpers to resolve inserts/cards products and variants from `window.giftWrapData`.
 * These are used by visualiser pages to set the "current visualiser variant" in UserInputContext.
 */

/**
 * Safely returns the inserts & cards products map from theme liquid JSON.
 * @returns {Record<string, any> | null} The products map, or null if missing.
 */
export function getInsertsAndCardsProducts() {
  const products = window?.giftWrapData?.insertsAndCards?.products || null;
  if (!products || typeof products !== 'object') return null;
  return /** @type {Record<string, any>} */ (products);
}

/**
 * Safely returns a single inserts & cards product by key.
 * @param {Object} options - The options for fetching a product.
 * @param {string} options.productKey - The product key (e.g. 'inserts', 'themedInserts').
 * @returns {any | null} The product, or null if missing.
 */
export function getInsertsAndCardsProduct({ productKey }) {
  const products = getInsertsAndCardsProducts();
  if (!products) return null;
  // eslint-disable-next-line security/detect-object-injection
  return products[productKey] || null;
}

/**
 * Attempts to find a variant in a product by matching a title-like string.
 * Falls back to matching `option1` if a fallback is provided.
 * @param {Object} options - The lookup options.
 * @param {any | null} options.product - The product object containing variants.
 * @param {string} options.variantTitle - The exact title to match.
 * @param {string | null} options.fallbackOption1 - Fallback option1 match (e.g. 'Text', 'Image Upload').
 * @returns {any | null} The matching variant or null.
 */
export function findVariantForProduct({ product, variantTitle, fallbackOption1 = null }) {
  if (!product || !Array.isArray(product.variants)) return null;
  if (typeof variantTitle !== 'string' || variantTitle.trim() === '') return null;

  const directMatch = product.variants.find((variant) => {
    const title = typeof variant?.title === 'string' ? variant.title : '';
    const publicTitle = typeof variant?.public_title === 'string' ? variant.public_title : '';
    const option1 = typeof variant?.option1 === 'string' ? variant.option1 : '';
    const name = typeof variant?.name === 'string' ? variant.name : '';

    return title === variantTitle || publicTitle === variantTitle || option1 === variantTitle || name === variantTitle;
  });

  if (directMatch) return directMatch;

  if (typeof fallbackOption1 === 'string' && fallbackOption1.trim() !== '') {
    const option1Match = product.variants.find((variant) => variant?.option1 === fallbackOption1 || variant?.title === fallbackOption1);
    return option1Match || null;
  }

  return null;
}

/**
 * Finds a variant by numeric ID within a product.
 * @param {Object} options - The lookup options.
 * @param {any | null} options.product - The product object containing variants.
 * @param {number | null} options.variantId - The numeric variant ID.
 * @returns {any | null} The matching variant or null.
 */
export function findVariantById({ product, variantId }) {
  if (!product || !Array.isArray(product.variants)) return null;
  if (typeof variantId !== 'number' || Number.isNaN(variantId)) return null;
  const match = product.variants.find((variant) => Number(variant?.id) === variantId);
  return match || null;
}


